import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BuildYourX1 } from '../components/builder/BuildYourX1';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { PrescriptionSection } from '../components/PrescriptionSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { ObjectionTeaser } from '../components/ObjectionTeaser';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest, scrollToPackCta } from '../utils/offerBus';
import { Offer } from '../data/product';
import { BUILDER_ID } from '../data/builder';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const objection = useObjectionTrigger();
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);

  // Any generic "#offer" buy link lands on the $29.95 builder first; the packs sit right below it.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const link = (event.target as HTMLElement | null)?.closest('a');
      if (!link || link.getAttribute('href') !== '#offer') return;
      event.preventDefault();
      document.getElementById(BUILDER_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    trackCustom('objection_selected', {
      objection: id
    });
    objection.close();
    // "Just looking" is respected: no funnel, no further interruption this session.
    if (id === 'looking') {
      trackCustom('objection_just_looking_selected');
      return;
    }
    setActiveObjection(id);
    if (choice.recommend && choice.recommend !== 'business') setSelectedOffer(choice.recommend);
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." 5OFF only applies to the standard single, so preselect it.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    objection.close();
    setSelectedOffer('single');
    scrollToPackCta('single');
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1840" data-mp="2mc76p65lnvxp">
      {showAnnouncementBar && <AnnouncementBar data-id="element-1841" data-mp="2qbpldk6tvas3" />}
      <SiteNav data-id="element-1842" data-mp="2qchaqd9u0c9x" />
      <main className="pb-24 lg:pb-0" data-id="element-1843" data-mp="2qawodaxo7zn5">
        {/* 1. Promise + product */}
        <Hero data-id="element-1844" data-mp="2va0yq4wc03f2" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-1845" data-mp="3020v5nd70e6w" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-1846" data-mp="351drcypyexm5" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-1847" data-mp="3a0qd1g539yn5" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-1848" data-mp="39ync412eel3n" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-1849" data-mp="3e34ubvt40qcb" />
        {/* 6b. Prescription ready, plus the RX Capability Insert add-on */}
        <PrescriptionSection data-id="element-1850" data-mp="12friyoo7xab4" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-1851" data-mp="ycv5tdhg66ek" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-1852" data-mp="te1thodwclfz" />
        {/* 7c. How to use it: the Quick Start Guide, step by step */}
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-1853" data-mp="ogrxtqzwzdjp" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-1854" data-mp="1m38t6qp37h2t" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-1855" data-mp="1h44g3oz1kz77" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1856" data-mp="1h68mcdkeyaan" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1857" data-mp="17e2ur4y2puvz" />
        {/* 11. The decision: build your own from $29.95 first ... */}
        <BuildYourX1 data-id="element-1858" data-mp="24tgownqgj58z" />
        {/* 11b. ... then, scrolling on, ready-made Complete Systems and multi-pack deals */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1859" data-mp="2028fvst1hlwc" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} onChooseAnother={objection.reopen} data-id="element-1860" data-mp="26jspnbo8ksh" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1861" data-mp="3kkixvi2st5wg" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1862" data-mp="3pjcdbg1nb6z9" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1863" data-mp="3aljykq3xamm4" />
      </main>
      <SiteFooter data-id="element-1864" data-mp="3aopog7kdlsiw" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1865" data-mp="3flogmkkhbpeg" />
      <ObjectionTeaser visible={objection.teaser} onExpand={objection.expand} onDismiss={objection.dismissTeaser} data-id="element-1866" data-mp="3fmhf6y9ab8li" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1867" data-mp="36l03ds2wtt2m" />
    </div>;
}